import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { getProductPricing, createKit, createKitBulk, updateKit, updateKitBulk, getKitDetail, getBatchWarehouses, getCategories, getWarehouses, getKitsList } from '../config/api';
import { toast } from 'react-toastify';
import useIsMobile from '../hooks/useIsMobile';
import { skuStatus, SKU_STATUS_STYLE, categoryTitle, skuFlags, kitChecks, KitChecks, hubDifferences, editorCatKey, HubDiffNote, KitScopeBar } from '../components/KitEditorTools';

// Compact inline dropdown for warehouse/kit switching
const SwitcherDropdown = ({ label, value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: '160px', maxWidth: '240px' }}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((p) => !p)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px',
          padding: '5px 10px',
          fontSize: '12px',
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          cursor: disabled ? 'default' : 'pointer',
          color: '#343a40',
          fontWeight: 500,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span style={{ color: '#6c757d', fontWeight: 400, marginRight: '4px' }}>{label}:</span>
          {disabled && options.length === 0 ? 'Loading…' : (selected?.label || '—')}
        </span>
        <span style={{ fontSize: '10px', color: '#6c757d', flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          zIndex: 1000,
          background: 'white',
          border: '1px solid #dee2e6',
          borderRadius: '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: '100%',
          maxHeight: '240px',
          overflowY: 'auto',
        }}>
          {options.map((opt) => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                background: opt.value === value ? '#f0f7ff' : 'white',
                color: opt.value === value ? '#0d6efd' : '#343a40',
                fontWeight: opt.value === value ? 600 : 400,
                borderBottom: '1px solid #f0f0f0',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={(e) => { if (opt.value !== value) e.currentTarget.style.background = '#f8f9fa'; }}
              onMouseOut={(e) => { if (opt.value !== value) e.currentTarget.style.background = 'white'; }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateKit = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { kitId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const fromWarehouseParam = searchParams.get('fromWarehouse');
  const rawWarehouseId = searchParams.get('warehouseId') || 'all';
  const isFromAllWarehouses = isEdit && (fromWarehouseParam === 'all' || rawWarehouseId === 'all');
  const [currentWarehouseId, setCurrentWarehouseId] = useState(
    isEdit ? (isFromAllWarehouses ? 'all' : (rawWarehouseId !== 'all' ? rawWarehouseId : '1')) : rawWarehouseId
  );

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Bulk Warehouse Selection Modal States
  const [showWarehouseModal, setShowWarehouseModal] = useState(false);
  const [allWarehousesList, setAllWarehousesList] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [kitBatchId, setKitBatchId] = useState(null);
  const [batchWarehouseIds, setBatchWarehouseIds] = useState([]);
  // Switcher: all warehouses this kit batch is deployed to
  const [batchWarehouses, setBatchWarehouses] = useState([]);
  const [batchSwitcherLoading, setBatchSwitcherLoading] = useState(false);
  // Switcher: selected warehouse id (drives the kit list)
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  // Switcher: all kits in the selected warehouse
  const [warehouseKits, setWarehouseKits] = useState([]);
  const [kitSwitcherLoading, setKitSwitcherLoading] = useState(false);

  const [kitData, setKitData] = useState({
    name: '',
    description: '',
    discount_percent: '',
    gst_percent: '',
    hsn_code: '',
    min_optional_count: 0,
    tags: '',
  });

  const [kitCategories, setKitCategories] = useState([]);
  const [skuSearch, setSkuSearch] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Hardcoded panel variants — admin assigns panel SKUs to each
  const PANEL_VARIANTS = [
    { id: 'bifacial_dcr', label: 'Bifacial DCR' },
    { id: 'topcon_dcr', label: 'TopCon DCR' },
  ];
  const PANEL_CATEGORY_ID = 1;
  // panelVariantProducts: { bifacial_dcr: [productId,...], topcon_dcr: [productId,...] }
  const [panelVariantProducts, setPanelVariantProducts] = useState({ bifacial_dcr: [], topcon_dcr: [] });
  // Payload of the kit as loaded in edit mode; compared at save time to find changed fields
  const initialPayloadRef = useRef(null);
  // Hubs pre-selected from the Kits matrix (?hubs=1,2,3)
  const hubsParam = searchParams.get('hubs');
  // Edit: the copy as loaded and its sibling copies at other hubs (same kit_batch_id)
  const [loadedKit, setLoadedKit] = useState(null);
  const [batchCopies, setBatchCopies] = useState([]);
  const [batchCopiesLoading, setBatchCopiesLoading] = useState(false);
  // Edit: where Save goes — 'one' (this hub), 'all' (every hub of this kit) or 'pick' (hub picker)
  const [scope, setScope] = useState(hubsParam ? 'pick' : 'all');
  // Hub whose stock and prices are shown next to each SKU (any hub; the admin can switch)
  const [stockHubs, setStockHubs] = useState([]);
  const [stockHubId, setStockHubId] = useState('');
  const [stockById, setStockById] = useState(null);
  const [stockLoading, setStockLoading] = useState(false);
  // SKU list filter: 'all' | 'notlive' | 'flagged'
  const [skuFilter, setSkuFilter] = useState('all');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData_30jan') || '{}');
    if (userData.role !== 'superAdmin' && !userData.access?.includes('marketing')) {
      window.location.href = '/orders';
      return;
    }
    loadCategories();
    loadStockHubs();
    if (isEdit && kitId) {
      // Reset switcher state so stale data from the previous kit doesn't flash
      setBatchWarehouses([]);
      setWarehouseKits([]);
      setSelectedWarehouseId(null);
      setPageLoading(true);
      loadKitForEdit();
    } else {
      loadProducts(currentWarehouseId);
    }
  }, [kitId]);

  useEffect(() => {
    if (stockHubId) loadStock(stockHubId);
  }, [stockHubId]);

  const loadStockHubs = async () => {
    try {
      const res = await getWarehouses();
      const list = (res && Array.isArray(res.data)) ? res.data : [];
      setStockHubs(list);
      // Create mode: show stock of the hub the kit is created for, else the first hub
      if (!isEdit && list.length) {
        setStockHubId((prev) => prev || (currentWarehouseId && currentWarehouseId !== 'all' ? String(currentWarehouseId) : String(list[0].id)));
      }
    } catch {
      // stock column stays hidden
    }
  };

  const loadStock = async (whId) => {
    setStockLoading(true);
    try {
      const res = await getProductPricing(whId);
      const list = (res && Array.isArray(res.data)) ? res.data : [];
      setStockById(Object.fromEntries(list.map((p) => [p.pid, p])));
    } catch {
      setStockById(null);
    } finally {
      setStockLoading(false);
    }
  };

  const loadKitForEdit = async () => {
    try {
      const response = await getKitDetail(kitId);
      if (response && response.data && response.data.kit) {
        const kit = response.data.kit;
        setLoadedKit(kit);
        if (kit.warehouse_id) setStockHubId(String(kit.warehouse_id));
        const bId = kit.kit_batch_id || kit.batch_id || kit.batchId || null;
        if (!bId) setScope('one');
        setBatchCopies([]);
        if (bId) {
          setBatchCopiesLoading(true);
          getKitsList().then((res) => {
            const all = (res && res.data && Array.isArray(res.data.kits)) ? res.data.kits : [];
            setBatchCopies(all.filter((k) => k.kit_batch_id === bId));
          }).catch(() => {}).finally(() => setBatchCopiesLoading(false));
        }
        if (bId) {
          setKitBatchId(bId);
          setBatchSwitcherLoading(true);
          getBatchWarehouses(bId).then((res) => {
            if (res && res.data && Array.isArray(res.data.warehouses)) {
              setBatchWarehouses(res.data.warehouses);
            }
          }).catch(() => {}).finally(() => setBatchSwitcherLoading(false));
        }
        // Set selected warehouse and load its kits for the kit switcher
        const whId = kit.warehouse_id || null;
        setSelectedWarehouseId(whId);
        if (whId) {
          setKitSwitcherLoading(true);
          getKitsList(whId).then((res) => {
            if (res && res.data && Array.isArray(res.data.kits)) {
              setWarehouseKits(res.data.kits);
            }
          }).catch(() => {}).finally(() => setKitSwitcherLoading(false));
        }

        const actualWhId = isFromAllWarehouses
          ? 'all'
          : (kit.warehouse_id ? String(kit.warehouse_id) : (currentWarehouseId !== 'all' ? currentWarehouseId : '1'));

        setCurrentWarehouseId(actualWhId);

        // Fetch products: if editing from All Warehouses, actualWhId is 'all' (getAllProducts?warehouseId=all)
        await loadProducts(actualWhId);

        const loadedKitData = {
          name: kit.name || '',
          description: kit.description || '',
          discount_percent: kit.discount_percent || '',
          gst_percent: kit.gst_percent || '',
          hsn_code: kit.hsn_code || '',
          min_optional_count: kit.min_optional_count || 0,
          tags: kit.tags || '',
        };
        setKitData(loadedKitData);
        if (kit.image) setImagePreview(kit.image);
        // Map categories
        const mappedCategories = (kit.categories || []).map((cat) => ({
          category_id: String(cat.category_id),
          is_mandatory: cat.is_mandatory,
          min_qty: cat.min_qty,
          max_qty: cat.max_qty,
          sort_order: cat.sort_order,
          label: cat.label || '',
          skus: (cat.skus || []).map((sku) => ({
            product_id: sku.product_id,
            product_name: sku.product ? sku.product.name : `Product #${sku.product_id}`,
            sku_code: sku.product ? sku.product.skuCode : '',
          })),
        }));
        setKitCategories(mappedCategories);
        // Open with every category collapsed; the headers carry the summary
        setCollapsedCategories(Object.fromEntries(mappedCategories.map((_, i) => [i, true])));

        // Load panel variant product assignments from stored variants
        const pvp = { bifacial_dcr: [], topcon_dcr: [] };
        if (kit.variants && kit.variants.length > 0) {
          kit.variants.forEach((v) => {
            if (pvp[v.id] !== undefined && v.products?.[String(PANEL_CATEGORY_ID)]) {
              pvp[v.id] = v.products[String(PANEL_CATEGORY_ID)];
            }
          });
        }
        setPanelVariantProducts(pvp);

        // Snapshot of the kit as loaded, so the multi-hub save sends only the fields that changed
        initialPayloadRef.current = buildKitPayload(loadedKitData, mappedCategories, pvp);
      }
    } catch (error) {
      toast.error('Failed to load kit data');
    } finally {
      setPageLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await getCategories();
      if (response && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  const loadProducts = async (whId = currentWarehouseId) => {
    setProductsLoading(true);
    try {
      const response = await getProductPricing(whId || 'all');
      if (response && response.data) {
        setAllProducts(response.data);
      }
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setProductsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setKitData({ ...kitData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image must be less than 10MB');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Category management
  const addCategory = () => {
    setKitCategories([
      ...kitCategories,
      {
        category_id: '',
        is_mandatory: true,
        min_qty: 1,
        max_qty: 1,
        sort_order: kitCategories.length,
        label: '',
        skus: [],
      },
    ]);
  };

  const removeCategory = (index) => {
    setKitCategories(kitCategories.filter((_, i) => i !== index));
  };

  const updateCategory = (index, field, value) => {
    const updated = [...kitCategories];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'category_id') {
      updated[index].skus = [];
    }
    setKitCategories(updated);
  };

  // SKU management
  const addSku = (catIndex, productId) => {
    const updated = [...kitCategories];
    const product = allProducts.find((p) => p.pid === parseInt(productId));
    if (!product) return;
    if (updated[catIndex].skus.find((s) => s.product_id === parseInt(productId))) {
      toast.warning('This product is already added');
      return;
    }
    updated[catIndex].skus.push({
      product_id: parseInt(productId),
      product_name: product.productName,
      sku_code: product.skuId,
    });
    setKitCategories(updated);
  };

  const removeSku = (catIndex, skuIndex) => {
    const updated = [...kitCategories];
    updated[catIndex].skus = updated[catIndex].skus.filter((_, i) => i !== skuIndex);
    setKitCategories(updated);
  };

  const removeSkusById = (catIndex, productIds) => {
    const updated = [...kitCategories];
    updated[catIndex] = { ...updated[catIndex], skus: updated[catIndex].skus.filter((s) => !productIds.includes(s.product_id)) };
    setKitCategories(updated);
  };

  const selectAllSkus = (catIndex) => {
    const updated = [...kitCategories];
    const cat = updated[catIndex];
    if (!cat || !cat.category_id) return;

    // Get available products for this row (excludes products assigned in other rows of the same category)
    let candidateProducts = getAvailableProductsForCategory(catIndex);

    // If search term is active for this row, filter candidates by search term
    const search = (skuSearch[catIndex] || '').toLowerCase().trim();
    if (search) {
      candidateProducts = candidateProducts.filter(
        (p) =>
          p.productName.toLowerCase().includes(search) ||
          (p.skuId || '').toLowerCase().includes(search)
      );
    }

    const existingIds = new Set(cat.skus.map((s) => s.product_id));
    const newSkus = candidateProducts
      .filter((p) => !existingIds.has(p.pid))
      .map((p) => ({
        product_id: p.pid,
        product_name: p.productName,
        sku_code: p.skuId,
      }));

    if (newSkus.length > 0) {
      updated[catIndex] = {
        ...cat,
        skus: [...cat.skus, ...newSkus],
      };
      setKitCategories(updated);
    }
  };

  const getAvailableProductsForCategory = (catIndex) => {
    const cat = kitCategories[catIndex];
    if (!cat || !cat.category_id) return [];

    // Products already assigned in other rows of the same category (not this row)
    const usedProductIds = new Set(
      kitCategories
        .filter((c, i) => i !== catIndex && String(c.category_id) === String(cat.category_id))
        .flatMap((c) => c.skus.map((s) => s.product_id))
    );

    return allProducts.filter(
      (p) => p.category_id === parseInt(cat.category_id) && !usedProductIds.has(p.pid)
    );
  };

  // Solar Panel and Inverter can only be added once; BOS and others can repeat
  const SINGLE_INSTANCE_CATEGORIES = [1, 2];

  const getUsedCategoryIds = () => {
    return kitCategories.map((c) => parseInt(c.category_id)).filter(Boolean);
  };

  const toggleCategoryCollapse = (index) => {
    setCollapsedCategories((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const togglePanelVariantProduct = (variantId, productId) => {
    setPanelVariantProducts((prev) => {
      const current = prev[variantId] ? [...prev[variantId]] : [];
      const idx = current.indexOf(productId);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(productId);
      }
      return { ...prev, [variantId]: current };
    });
  };

  const selectAllPanelVariantProducts = (variantId, targetSkus = null) => {
    const panelCat = kitCategories.find((c) => parseInt(c.category_id) === PANEL_CATEGORY_ID);
    if (!panelCat) return;
    const skusToSelect = targetSkus || panelCat.skus;
    const idsToSelect = skusToSelect.map((s) => s.product_id);
    setPanelVariantProducts((prev) => {
      const current = prev[variantId] || [];
      const combined = Array.from(new Set([...current, ...idsToSelect]));
      return { ...prev, [variantId]: combined };
    });
  };

  const clearPanelVariantProducts = (variantId, targetSkus = null) => {
    setPanelVariantProducts((prev) => {
      if (!targetSkus) {
        return { ...prev, [variantId]: [] };
      }
      const idsToRemove = targetSkus.map((s) => s.product_id);
      return { ...prev, [variantId]: (prev[variantId] || []).filter((id) => !idsToRemove.includes(id)) };
    });
  };

  const fetchWarehousesForModal = async () => {
    setWarehousesLoading(true);
    try {
      const res = await getWarehouses();
      let fullList = [];
      if (res && res.data) {
        fullList = res.data;
        setAllWarehousesList(fullList);
      }

      // If kit has a batch_id, fetch deployed warehouses for this batch
      if (kitBatchId) {
        try {
          const batchRes = await getBatchWarehouses(kitBatchId);
          if (batchRes && batchRes.data && Array.isArray(batchRes.data.warehouses) && batchRes.data.warehouses.length > 0) {
            const deployedIds = batchRes.data.warehouses.map((w) => Number(w.warehouseId || w.warehouse_id));
            setBatchWarehouseIds(deployedIds);
            // Hubs picked on the Kits matrix win over "all hubs of this kit"
            const matrixHubIds = (hubsParam || '').split(',').map(Number).filter(Boolean);
            setSelectedWarehouseIds(matrixHubIds.length ? matrixHubIds : deployedIds);
            return;
          }
        } catch (batchErr) {
          console.warn('Could not fetch batch warehouses:', batchErr);
        }
      }

      // Default pre-selection: select all active warehouses if no batch warehouses returned
      setSelectedWarehouseIds(fullList.map((w) => w.id));
    } catch (err) {
      toast.error('Failed to load warehouses list');
    } finally {
      setWarehousesLoading(false);
    }
  };

  const selectableWarehouses = allWarehousesList;

  // Warehouse switcher handler — fetch kits for selected warehouse, auto-navigate to first kit
  const handleWarehouseSwitch = async (warehouseId) => {
    if (!warehouseId || warehouseId === selectedWarehouseId) return;
    setSelectedWarehouseId(warehouseId);
    setWarehouseKits([]);
    setKitSwitcherLoading(true);
    try {
      const res = await getKitsList(warehouseId);
      const kits = (res && res.data && Array.isArray(res.data.kits)) ? res.data.kits : [];
      setWarehouseKits(kits);
      // Navigate to the first kit of the selected warehouse
      if (kits.length > 0) {
        navigate(`/kits/edit/${kits[0].id}`);
      }
    } catch {
      // silently fail — kit list stays empty
    } finally {
      setKitSwitcherLoading(false);
    }
  };

  const handleToggleSelectAllWarehouses = (e) => {
    if (e.target.checked) {
      setSelectedWarehouseIds(allWarehousesList.map((w) => w.id));
    } else {
      setSelectedWarehouseIds([]);
    }
  };

  const handleToggleSingleWarehouse = (whId) => {
    setSelectedWarehouseIds((prev) =>
      prev.includes(whId) ? prev.filter((id) => id !== whId) : [...prev, whId]
    );
  };

  const buildKitPayload = (kd, cats, pvp) => ({
    name: kd.name.trim(),
    category_id: 4,
    description: kd.description.trim() || null,
    discount_percent: parseFloat(kd.discount_percent) || 0,
    gst_percent: parseFloat(kd.gst_percent) || null,
    hsn_code: kd.hsn_code || null,
    tags: kd.tags.trim() || null,
    min_optional_count: parseInt(kd.min_optional_count) || 0,
    warehouse_id: currentWarehouseId && currentWarehouseId !== 'all' ? parseInt(currentWarehouseId, 10) : undefined,
    // Always include hardcoded variants with panel product assignments
    variants: PANEL_VARIANTS.map((v) => ({
      id: v.id,
      label: v.label,
      products: { [PANEL_CATEGORY_ID]: pvp[v.id] || [] },
    })),
    categories: cats.map((cat, index) => ({
      category_id: parseInt(cat.category_id),
      is_mandatory: cat.is_mandatory,
      min_qty: parseInt(cat.min_qty) || 1,
      max_qty: parseInt(cat.max_qty) || 1,
      sort_order: index,
      label: parseInt(cat.category_id) === 3 ? (cat.label || '').trim() : null,
      skus: cat.skus.map((sku) => ({ product_id: sku.product_id })),
    })),
  });

  // Fields the multi-hub save can send on its own. Categories and panel variants travel together.
  const KIT_FIELD_LABELS = {
    name: 'Kit name',
    description: 'Description',
    discount_percent: 'Discount %',
    gst_percent: 'GST %',
    hsn_code: 'HSN code',
    tags: 'Tags',
    min_optional_count: 'Min optional categories',
    categories: 'Categories & SKUs',
    variants: 'Panel variants',
  };

  // Changed field keys versus the loaded kit; null when there is no snapshot (send everything)
  const getChangedFields = (payload) => {
    const initial = initialPayloadRef.current;
    if (!initial || !payload) return null;
    const changed = Object.keys(KIT_FIELD_LABELS).filter(
      (key) => JSON.stringify(payload[key]) !== JSON.stringify(initial[key])
    );
    if (changed.includes('categories') && !changed.includes('variants')) changed.push('variants');
    if (changed.includes('variants') && !changed.includes('categories')) changed.push('categories');
    return changed;
  };

  const handleBulkUpdateSubmit = async () => {
    if (!selectedWarehouseIds || selectedWarehouseIds.length === 0) {
      toast.error('Please select at least one warehouse to apply changes');
      return;
    }
    setShowWarehouseModal(false);
    await saveToHubs(pendingPayload, selectedWarehouseIds);
  };

  // Only the fields changed since load (everything when there is no snapshot)
  const pickChanged = (payload) => {
    const changedFields = getChangedFields(payload);
    return changedFields
      ? changedFields.reduce((acc, key) => ({ ...acc, [key]: payload[key] }), {})
      : payload;
  };

  const saveToHubs = async (payload, warehouseIds) => {
    setLoading(true);
    try {
      const bulkPayload = {
        ...pickChanged(payload),
        warehouse_ids: warehouseIds,
      };
      const targetBatchId = kitBatchId || kitId;
      await updateKitBulk(targetBatchId, bulkPayload, imageFile);
      toast.success(`Kit updated for ${warehouseIds.length} warehouses successfully!`);
      navigate('/kits');
    } catch (error) {
      toast.error(error.message || 'Failed to update kit for selected warehouses');
    } finally {
      setLoading(false);
    }
  };

  const saveThisHubOnly = async (payload) => {
    setLoading(true);
    try {
      await updateKit(kitId, pickChanged(payload), imageFile);
      toast.success(`Kit updated for ${loadedKit && loadedKit.warehouse ? loadedKit.warehouse.name : 'this hub'}`);
      navigate('/kits');
    } catch (error) {
      toast.error(error.message || 'Failed to update kit');
    } finally {
      setLoading(false);
    }
  };

  // Warehouse ids of every copy of this kit, for the "All hubs" scope
  const batchHubIds = batchCopies.length
    ? batchCopies.map((k) => k.warehouse_id).filter(Boolean)
    : batchWarehouses.map((w) => Number(w.warehouseId || w.warehouse_id)).filter(Boolean);
  const scopeHubName = loadedKit && loadedKit.warehouse ? loadedKit.warehouse.name : 'this hub';
  const showScopeBar = isEdit && !!kitBatchId && batchHubIds.length > 1;
  const effectiveScope = showScopeBar ? scope : (isEdit && !kitBatchId ? 'one' : 'pick');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!kitData.name.trim()) {
      toast.error('Kit name is required');
      return;
    }
    if (!kitData.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!kitData.hsn_code || !kitData.hsn_code.trim()) {
      toast.error('HSN Code is required');
      return;
    }
    if (kitData.hsn_code.trim().length < 6) {
      toast.error('HSN Code must be at least 6 digits');
      return;
    }
    if (kitData.discount_percent === '' || kitData.discount_percent === null || kitData.discount_percent === undefined) {
      toast.error('Kit Discount % is required');
      return;
    }
    if (!kitData.gst_percent) {
      toast.error('GST % is required');
      return;
    }
    if (!kitData.tags || !kitData.tags.trim()) {
      toast.error('Tags are required');
      return;
    }
    if (!imageFile && !imagePreview) {
      toast.error('Kit image is required');
      return;
    }

    // Validate min_optional_count against actual optional categories
    const optionalCatCount = kitCategories.filter((c) => !c.is_mandatory).length;
    const minOptional = parseInt(kitData.min_optional_count) || 0;
    if (minOptional > optionalCatCount) {
      toast.error(`Min Optional Categories (${minOptional}) cannot exceed the number of optional categories added (${optionalCatCount}). Either reduce the value or mark some categories as Optional.`);
      return;
    }
    if (kitCategories.length === 0) {
      toast.error('Add at least one category');
      return;
    }
    for (let i = 0; i < kitCategories.length; i++) {
      const cat = kitCategories[i];
      if (!cat.category_id) {
        toast.error(`Select a category for row ${i + 1}`);
        return;
      }
      if (parseInt(cat.category_id) === 3 && (!cat.label || !cat.label.trim())) {
        toast.error(`Label is required for BOS category in row ${i + 1}`);
        return;
      }
      if (cat.skus.length === 0) {
        toast.error(`Add at least one eligible SKU for category row ${i + 1}`);
        return;
      }
    }

    // If panel category is present, validate variant product assignments
    const panelCat = kitCategories.find((c) => parseInt(c.category_id) === PANEL_CATEGORY_ID);
    if (panelCat && panelCat.skus.length > 0) {
      const totalAssigned = PANEL_VARIANTS.reduce((sum, v) => sum + (panelVariantProducts[v.id]?.length || 0), 0);
      if (totalAssigned === 0) {
        toast.error('Please assign panel SKUs to Bifacial DCR or TopCon DCR variants');
        return;
      }
    }

    const payload = buildKitPayload(kitData, kitCategories, panelVariantProducts);

    // Edit: save where the scope bar says; "Pick hubs" opens the warehouse selection modal
    if (isEdit && effectiveScope === 'one') {
      await saveThisHubOnly(payload);
      return;
    }
    if (isEdit && effectiveScope === 'all') {
      await saveToHubs(payload, batchHubIds);
      return;
    }
    if (isEdit) {
      setPendingPayload(payload);
      await fetchWarehousesForModal();
      setShowWarehouseModal(true);
      return;
    }

    setLoading(true);
    try {
      if (isEdit && kitId) {
        await updateKit(kitId, payload, imageFile);
        toast.success('Kit updated successfully!');
      } else {
        if (!currentWarehouseId || currentWarehouseId === 'all' || payload.warehouse_ids) {
          await createKitBulk(payload, imageFile);
          toast.success('Kit created for all warehouses successfully!');
        } else {
          await createKit(payload, imageFile);
          toast.success('Kit created successfully!');
        }
      }
      navigate('/kits');
    } catch (error) {
      toast.error(error.message || `Failed to ${isEdit ? 'update' : 'create'} kit`);
    } finally {
      setLoading(false);
    }
  };

  const optionalCount = kitCategories.filter((c) => !c.is_mandatory).length;

  // Stock hub, checks and hub differences for the editor
  const stockHub = stockHubs.find((w) => String(w.id) === String(stockHubId));
  const stockHubName = stockHub ? stockHub.name : '';
  const checks = kitChecks({
    kitName: kitData.name,
    kitCategories,
    categories,
    panelVariantProducts,
    minOptional: kitData.min_optional_count,
    stockById: stockLoading ? null : stockById,
    stockHubName,
  });
  const variantIds = new Set(Object.values(panelVariantProducts).flat());
  const showHubNotes = showScopeBar && effectiveScope !== 'one';
  const diffs = showScopeBar && loadedKit && batchCopies.length ? hubDifferences(loadedKit, batchCopies) : null;
  const fieldNote = (key) => (showHubNotes && diffs ? <HubDiffNote hubs={diffs.fields[key]} /> : null);
  const saveLabel = !isEdit ? 'Create Kit'
    : effectiveScope === 'one' ? `Save for ${scopeHubName}`
    : effectiveScope === 'all' ? `Save for ${batchHubIds.length} hubs`
    : 'Update Kit';

  const jumpToCategory = (catIndex) => {
    setCollapsedCategories((prev) => ({ ...prev, [catIndex]: false }));
    setTimeout(() => {
      const el = document.getElementById(`kit-cat-${catIndex}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  if (pageLoading) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', padding: '80px 20px' }}>
          <div style={{ display: 'inline-block', width: '40px', height: '40px', border: '4px solid #e9ecef', borderTop: '4px solid #0d6efd', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '16px' }} />
          <p style={{ color: '#6c757d', fontSize: '15px', margin: 0 }}>Loading kit data...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ position: 'relative', paddingBottom: isMobile ? undefined : '72px' }}>
      {/* Submission Loading Overlay */}
      {loading && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(3px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '14px',
          }}
        >
          <div
            style={{
              width: '48px',
              height: '48px',
              border: '4px solid #e9ecef',
              borderTop: '4px solid #198754',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
          <div style={{ fontWeight: 600, fontSize: '16px', color: '#212529' }}>
            {isEdit ? 'Updating Custom Kit...' : 'Creating Custom Kit...'}
          </div>
          <div style={{ fontSize: '13px', color: '#6c757d' }}>
            Please wait while we save your kit configuration and upload assets.
          </div>
        </div>
      )}

      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/kits">Custom Kits</Link> / {isEdit ? 'Edit Kit' : 'Create New Kit'}
            </div>
            <h1>{isEdit ? 'Edit Kit' : 'Create New Kit'}</h1>
            {isEdit && (batchWarehouses.length > 1 || warehouseKits.length >= 1) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                {batchWarehouses.length > 1 && (
                  <SwitcherDropdown
                    label="Warehouse"
                    value={selectedWarehouseId ? String(selectedWarehouseId) : ''}
                    options={batchWarehouses.map((bw) => ({
                      value: String(bw.warehouseId),
                      label: bw.warehouseName
                        ? `${bw.warehouseName}${bw.warehouseCity ? ` (${bw.warehouseCity})` : ''}`
                        : `Warehouse ${bw.warehouseId}`,
                    }))}
                    onChange={(whId) => handleWarehouseSwitch(whId)}
                    disabled={batchSwitcherLoading || kitSwitcherLoading}
                  />
                )}
                {(warehouseKits.length >= 1 || kitSwitcherLoading) && (
                  <SwitcherDropdown
                    label="Kit"
                    value={String(kitId)}
                    options={warehouseKits.map((k) => ({
                      value: String(k.id),
                      label: k.name,
                    }))}
                    onChange={(selectedKitId) => { if (selectedKitId !== String(kitId)) navigate(`/kits/edit/${selectedKitId}`); }}
                    disabled={kitSwitcherLoading}
                  />
                )}
              </div>
            )}
          </div>
          <div className="order-actions">
            <Link to="/kits" className="btn btn-secondary" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}>Cancel</Link>
            <button
              type="submit"
              form="create-kit-form"
              disabled={loading}
              className="btn btn-success"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              {loading && (
                <span
                  style={{
                    display: 'inline-block',
                    width: '14px',
                    height: '14px',
                    border: '2px solid rgba(255,255,255,0.4)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              )}
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : saveLabel}
            </button>
          </div>
        </div>

        <form id="create-kit-form" onSubmit={handleSubmit}>
          {showScopeBar && (
            <KitScopeBar
              scope={effectiveScope}
              onChange={setScope}
              hubName={scopeHubName}
              hubCount={batchHubIds.length}
              diffs={diffs}
              loading={batchCopiesLoading}
            />
          )}
          <KitChecks checks={checks} stockHubName={stockLoading ? '' : stockHubName} onJump={jumpToCategory} />
          {/* Basic Information */}
          <div className="card">
            <h3 className="section-title">Basic Information</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Category *</label>
                <select className="form-control" value={4} disabled>
                  <option value={4}>Kit</option>
                </select>
              </div>
              <div className="info-item">
                <label className="info-label">Kit Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={kitData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. ZunSolar 5 kW DCR"
                  required
                />
                {fieldNote('name')}
              </div>
              <div className="info-item">
                <label className="info-label">Kit Discount % *</label>
                <input
                  type="number"
                  name="discount_percent"
                  className="form-control"
                  value={kitData.discount_percent}
                  onChange={handleInputChange}
                  placeholder="e.g. 10"
                  min="0"
                  max="100"
                  step="0.01"
                  required
                />
                {fieldNote('discount_percent')}
              </div>
              <div className="info-item">
                <label className="info-label">GST % *</label>
                <input
                  type="number"
                  name="gst_percent"
                  className="form-control"
                  value={kitData.gst_percent}
                  onChange={handleInputChange}
                  placeholder="e.g. 5"
                  min="0"
                  max="28"
                  step="0.01"
                />
                {fieldNote('gst_percent')}
              </div>
              <div className="info-item">
                <label className="info-label">Tags *</label>
                <input
                  type="text"
                  name="tags"
                  className="form-control"
                  value={kitData.tags}
                  onChange={handleInputChange}
                  placeholder="Comma separated tags (e.g., solar, panel, 5kw)"
                  required
                />
                {fieldNote('tags')}
              </div>
              <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                <label className="info-label">Description *</label>
                <textarea
                  name="description"
                  className="form-control"
                  value={kitData.description}
                  onChange={handleInputChange}
                  placeholder="Short description for kit card display (e.g. Complete solar kit with panels, inverter & accessories)"
                  rows={2}
                  maxLength={200}
                  style={{ resize: 'vertical' }}
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>{kitData.description.length}/200 characters</small>
              </div>
              <div className="info-item">
                <label className="info-label">HSN Code *</label>
                <input
                  type="text"
                  name="hsn_code"
                  className="form-control"
                  value={kitData.hsn_code}
                  onChange={handleInputChange}
                  placeholder="e.g. 854140"
                  minLength={6}
                  disabled={isEdit}
                  required
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>
                  {isEdit ? 'HSN Code is frozen and cannot be edited' : 'Minimum 6 digits'}
                </small>
              </div>
              <div className="info-item">
                <label className="info-label">Min Optional Categories</label>
                <input
                  type="number"
                  name="min_optional_count"
                  className="form-control"
                  value={kitData.min_optional_count}
                  onChange={handleInputChange}
                  placeholder="0"
                  min="0"
                  max={optionalCount || 99}
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>
                  Customer must select at least this many optional categories
                </small>
                {fieldNote('min_optional_count')}
              </div>
            </div>
          </div>

          {/* Kit Image */}
          <div className="card">
            <h3 className="section-title">Kit Image</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Upload Image *</label>
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleImageChange}
                />
                <small style={{ color: '#6c757d', fontSize: '11px' }}>Max 10MB. JPEG, PNG supported.</small>
              </div>
              {imagePreview && imagePreview.startsWith('http') || imagePreview && imagePreview.startsWith('blob') ? (
                <div className="info-item">
                  <label className="info-label">Preview</label>
                  <img
                    src={imagePreview}
                    alt="Kit"
                    style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #dee2e6' }}
                  />
                </div>
              ) : null}
            </div>
          </div>

          {/* Kit Categories */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' }}>
              <h3 className="section-title" style={{ marginBottom: '0' }}>Kit Categories & Eligible SKUs</h3>
              <button type="button" onClick={addCategory} className="btn btn-primary" style={{ fontSize: '12px', padding: '8px 16px' }}>
                + Add Category
              </button>
            </div>

            {/* Stock hub + SKU filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px', fontSize: '13px' }}>
              <label style={{ fontWeight: 600, margin: 0 }} htmlFor="kit-stock-hub">Stock at</label>
              <select
                id="kit-stock-hub"
                className="form-control"
                value={stockHubId}
                onChange={(e) => setStockHubId(e.target.value)}
                style={{ width: '100%', maxWidth: '260px' }}
              >
                {!stockHubId && <option value="">Choose a hub</option>}
                {stockHubs.map((wh) => (
                  <option key={wh.id} value={wh.id}>
                    {wh.name}{wh.city ? ` - ${wh.city}` : ''}{(wh.hub_type === 'VIRTUAL' || wh.hubType === 'VIRTUAL') ? ' (Virtual Hub)' : ''}
                  </option>
                ))}
              </select>
              {stockLoading && <span style={{ color: '#6c757d' }}>Loading stock…</span>}
              <div style={{ display: 'inline-flex', border: '1px solid #dee2e6', borderRadius: '6px', overflow: 'hidden', marginLeft: isMobile ? 0 : 'auto' }}>
                {[['all', 'All SKUs'], ['notlive', 'Not live here'], ['flagged', 'Flagged']].map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSkuFilter(value)}
                    style={{ border: 'none', padding: '6px 12px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: skuFilter === value ? '#0d6efd' : 'white', color: skuFilter === value ? 'white' : '#495057' }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {productsLoading && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#0d6efd', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <div style={{ width: '18px', height: '18px', border: '2px solid #b6d4fe', borderTop: '2px solid #0d6efd', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <span style={{ fontSize: '13px', fontWeight: 500 }}>Loading product catalog...</span>
              </div>
            )}

            {kitCategories.length === 0 && !productsLoading && (
              <div style={{ padding: '24px', textAlign: 'center', color: '#6c757d', background: '#f8f9fa', borderRadius: '6px' }}>
                No categories added yet. Click "+ Add Category" to start building the kit.
              </div>
            )}

            {kitCategories.map((cat, catIndex) => {
              const needQty = parseInt(cat.min_qty) || 1;
              const liveSkus = stockById && !stockLoading ? cat.skus.filter((s) => skuStatus(stockById[s.product_id], needQty) === 'live').length : null;
              const catHubs = showHubNotes && diffs ? diffs.cats[editorCatKey(cat)] : null;
              return (
              <div key={catIndex} id={`kit-cat-${catIndex}`} style={{ marginBottom: '15px', padding: '15px', border: '1px solid #e9ecef', borderRadius: '6px', background: cat.is_mandatory ? '#f8fbff' : '#fffdf5' }}>
                {/* Category header — collapsible */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '8px' }} onClick={() => toggleCategoryCollapse(catIndex)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', minWidth: 0 }}>
                    <span style={{ fontSize: '14px', transition: 'transform 0.2s', transform: collapsedCategories[catIndex] ? 'rotate(-90deg)' : 'rotate(0deg)' }}>▼</span>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>
                      {categoryTitle(cat, categories, catIndex)}
                    </span>
                    <span style={{ fontSize: '11px', color: cat.is_mandatory ? '#1565c0' : '#e65100', background: cat.is_mandatory ? '#e3f2fd' : '#fff3e0', padding: '2px 8px', borderRadius: '8px' }}>
                      {cat.is_mandatory ? 'Mandatory' : 'Optional'}
                    </span>
                    {cat.category_id && <span style={{ fontSize: '11px', color: '#6c757d' }}>qty {cat.min_qty}–{cat.max_qty}</span>}
                    {cat.skus.length > 0 && <span style={{ fontSize: '11px', color: '#6c757d' }}>{cat.skus.length} SKUs</span>}
                    {liveSkus !== null && cat.skus.length > 0 && (
                      <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '8px', background: liveSkus ? '#d1e7dd' : '#f8d7da', color: liveSkus ? '#0f5132' : '#842029' }}>
                        {liveSkus}/{cat.skus.length} live at {stockHubName}
                      </span>
                    )}
                    {parseInt(cat.category_id) === PANEL_CATEGORY_ID && cat.skus.length > 0 && (
                      <span style={{ fontSize: '11px', color: '#6c757d' }}>
                        Bifacial {(panelVariantProducts.bifacial_dcr || []).length} · TopCon {(panelVariantProducts.topcon_dcr || []).length} · no variant {cat.skus.filter((s) => !variantIds.has(s.product_id)).length}
                      </span>
                    )}
                    {catHubs && <span style={{ fontSize: '11px', color: '#b35c00' }}>differs at {catHubs.length} {catHubs.length === 1 ? 'hub' : 'hubs'}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeCategory(catIndex); }}
                    className="btn btn-secondary"
                    style={{ fontSize: '11px', padding: '4px 10px', backgroundColor: '#dc3545', borderColor: '#dc3545', color: 'white' }}
                  >
                    Remove
                  </button>
                </div>

                {!collapsedCategories[catIndex] && (
                  <div style={{ marginTop: '12px' }}>
                {/* Category row config */}
                <div className="order-info" style={{ marginBottom: '12px' }}>
                  <div className="info-item">
                    <label className="info-label">Category *</label>
                    <select
                      className="form-control"
                      value={cat.category_id}
                      onChange={(e) => updateCategory(catIndex, 'category_id', e.target.value)}
                    >
                      <option value="">Select category</option>
                      {categories.filter((c) => c.id !== 4).map((c) => (
                        <option
                          key={c.id}
                          value={c.id}
                          disabled={SINGLE_INSTANCE_CATEGORIES.includes(c.id) && getUsedCategoryIds().includes(c.id) && parseInt(cat.category_id) !== c.id}
                        >
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="info-item">
                    <label className="info-label">Type</label>
                    <select
                      className="form-control"
                      value={cat.is_mandatory ? 'mandatory' : 'optional'}
                      onChange={(e) => updateCategory(catIndex, 'is_mandatory', e.target.value === 'mandatory')}
                    >
                      <option value="mandatory">Mandatory</option>
                      <option value="optional">Optional</option>
                    </select>
                  </div>
                  <div className="info-item">
                    <label className="info-label">Min Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      value={cat.min_qty}
                      onChange={(e) => updateCategory(catIndex, 'min_qty', e.target.value)}
                      min="1"
                    />
                  </div>
                  <div className="info-item">
                    <label className="info-label">Max Qty</label>
                    <input
                      type="number"
                      className="form-control"
                      value={cat.max_qty}
                      onChange={(e) => updateCategory(catIndex, 'max_qty', e.target.value)}
                      min="1"
                    />
                  </div>
                  {parseInt(cat.category_id) === 3 && (
                    <div className="info-item">
                      <label className="info-label">Label *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={cat.label || ''}
                        onChange={(e) => updateCategory(catIndex, 'label', e.target.value)}
                        placeholder="e.g. ACDB, DCDB, Earthing Kit"
                        maxLength={100}
                        required
                      />
                      <small style={{ color: '#6c757d', fontSize: '11px' }}>Required label for BOS items</small>
                    </div>
                  )}
                </div>

                {/* SKU selection */}
                {cat.category_id && (
                  <div style={{ borderTop: '1px solid #e9ecef', paddingTop: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
                      <label className="info-label" style={{ marginBottom: 0, fontSize: '12px' }}>
                        Eligible SKUs ({cat.skus.length} selected)
                      </label>
                      <button type="button" onClick={() => selectAllSkus(catIndex)} className="btn btn-secondary" style={{ fontSize: isMobile ? '13px' : '11px', padding: isMobile ? '8px 14px' : '4px 10px' }}>
                        Select All
                      </button>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '8px' }}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="+ Search & add a product..."
                        value={skuSearch[catIndex] !== undefined ? (skuSearch[catIndex] || '') : ''}
                        onChange={(e) => setSkuSearch({ ...skuSearch, [catIndex]: e.target.value })}
                        onFocus={() => setSkuSearch((prev) => ({ ...prev, [catIndex]: prev[catIndex] || '' }))}
                        onBlur={() => setTimeout(() => setSkuSearch((prev) => ({ ...prev, [catIndex]: undefined })), 150)}
                      />
                      {skuSearch[catIndex] !== undefined && (() => {
                        const search = (skuSearch[catIndex] || '').toLowerCase();
                        const filtered = getAvailableProductsForCategory(catIndex)
                          .filter((p) => !cat.skus.find((s) => s.product_id === p.pid))
                          .filter((p) => !search || p.productName.toLowerCase().includes(search) || (p.skuId || '').toLowerCase().includes(search));

                        return (
                          <div
                            onMouseDown={(e) => e.preventDefault()}
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 10,
                              background: 'white',
                              border: '1px solid #ced4da',
                              borderTop: 'none',
                              borderRadius: '0 0 4px 4px',
                              maxHeight: '200px',
                              overflowY: 'auto',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                            }}
                          >
                            {filtered.length > 0 ? (
                              filtered.slice(0, 40).map((p) => (
                                <div
                                  key={p.pid}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addSku(catIndex, p.pid);
                                  }}
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                  }}
                                  onMouseOver={(e) => (e.currentTarget.style.background = '#f0f7ff')}
                                  onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                                >
                                  <span>{p.productName} <span style={{ color: '#6c757d', fontSize: '11px' }}>({p.skuId})</span></span>
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
                                    {stockById && !stockLoading && (() => {
                                      const st = skuStatus(stockById[p.pid], parseInt(cat.min_qty) || 1);
                                      return <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 7px', borderRadius: '8px', background: SKU_STATUS_STYLE[st].background, color: SKU_STATUS_STYLE[st].color }}>{SKU_STATUS_STYLE[st].label} at {stockHubName}</span>;
                                    })()}
                                    <span style={{ fontSize: '11px', color: '#0d6efd', fontWeight: 600 }}>+ Add</span>
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '10px 12px', fontSize: '12px', color: '#6c757d', fontStyle: 'italic' }}>
                                {search ? `No products match "${skuSearch[catIndex]}"` : 'All eligible products selected'}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* SKU rows: status at the stock hub, flags, panel variants */}
                    {(() => {
                      const isPanel = parseInt(cat.category_id) === PANEL_CATEGORY_ID;
                      const rows = cat.skus.map((sku, skuIndex) => {
                        const product = stockById ? stockById[sku.product_id] : null;
                        const status = stockById && !stockLoading ? skuStatus(product, needQty) : null;
                        const flags = skuFlags({ sku, cat, kitName: kitData.name, variantIds: isPanel ? variantIds : null });
                        return { sku, skuIndex, product, status, flags };
                      }).filter((r) => (
                        skuFilter === 'all'
                        || (skuFilter === 'notlive' && r.status && r.status !== 'live')
                        || (skuFilter === 'flagged' && r.flags.length > 0)
                      ));
                      return (
                        <div>
                          {isPanel && cat.skus.length > 0 && (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', fontSize: '12px', color: '#6c757d', marginBottom: '6px' }}>
                              <span>Variants for {skuFilter === 'all' ? 'all panels' : 'the panels shown'}:</span>
                              {PANEL_VARIANTS.map((v) => (
                                <span key={v.id} style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                                  <strong style={{ color: '#495057' }}>{v.label}</strong>
                                  <button type="button" onClick={() => selectAllPanelVariantProducts(v.id, skuFilter === 'all' ? null : rows.map((r) => r.sku))} style={{ fontSize: '11px', padding: '1px 8px', background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '3px', cursor: 'pointer' }}>All</button>
                                  <button type="button" onClick={() => clearPanelVariantProducts(v.id, skuFilter === 'all' ? null : rows.map((r) => r.sku))} style={{ fontSize: '11px', padding: '1px 8px', background: '#e9ecef', border: '1px solid #ced4da', borderRadius: '3px', cursor: 'pointer' }}>Clear</button>
                                </span>
                              ))}
                            </div>
                          )}
                          {skuFilter !== 'all' && rows.length > 0 && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '6px' }}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 10px' }}
                                onClick={() => {
                                  if (window.confirm(`Remove these ${rows.length} SKUs from ${categoryTitle(cat, categories, catIndex)}? Nothing is saved until you press Save.`)) {
                                    removeSkusById(catIndex, rows.map((r) => r.sku.product_id));
                                  }
                                }}
                              >
                                Remove these {rows.length}
                              </button>
                            </div>
                          )}
                          {rows.length === 0 && cat.skus.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#6c757d', fontStyle: 'italic', padding: '4px 0' }}>
                              {skuFilter === 'notlive' ? `Every SKU here is live at ${stockHubName}.` : 'No flagged SKUs here.'}
                            </div>
                          )}
                          {rows.map(({ sku, skuIndex, product, status, flags }) => (
                            <div
                              key={sku.product_id}
                              style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '6px 0', borderBottom: '1px solid #f1f3f5', fontSize: '12px', opacity: status && status !== 'live' ? 0.75 : 1 }}
                            >
                              <span style={{ flex: '1 1 320px', minWidth: 0 }}>
                                <span>{sku.product_name}</span>
                                {sku.sku_code && <span style={{ fontSize: '11px', color: '#6c757d', fontFamily: 'monospace', marginLeft: '6px' }}>{sku.sku_code}</span>}
                                {flags.map((f) => (
                                  <span key={f} style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '8px', background: f === 'Non-DCR' ? '#f8d7da' : '#fff3cd', color: f === 'Non-DCR' ? '#842029' : '#856404' }}>{f}</span>
                                ))}
                              </span>
                              {status && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flex: '0 0 auto' }}>
                                  <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 7px', borderRadius: '8px', background: SKU_STATUS_STYLE[status].background, color: SKU_STATUS_STYLE[status].color }}>{SKU_STATUS_STYLE[status].label}</span>
                                  {product && <span style={{ color: '#6c757d', minWidth: '56px', textAlign: 'right' }}>{Number(product.inStock) || 0} in stock</span>}
                                  {product && product.priceSlabs && product.priceSlabs[0] && (
                                    <span style={{ color: '#495057', minWidth: '64px', textAlign: 'right' }}>₹{Math.round(product.priceSlabs[0].price).toLocaleString('en-IN')}</span>
                                  )}
                                </span>
                              )}
                              {isPanel && (
                                <span style={{ display: 'inline-flex', gap: '4px', flex: '0 0 auto' }}>
                                  {PANEL_VARIANTS.map((v) => {
                                    const on = (panelVariantProducts[v.id] || []).includes(sku.product_id);
                                    return (
                                      <button
                                        key={v.id}
                                        type="button"
                                        title={v.label}
                                        aria-pressed={on}
                                        onClick={() => togglePanelVariantProduct(v.id, sku.product_id)}
                                        style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', cursor: 'pointer', border: on ? '1px solid #7b1fa2' : '1px solid #dee2e6', background: on ? '#e1bee7' : 'white', color: on ? '#4a148c' : '#adb5bd' }}
                                      >
                                        {v.id === 'bifacial_dcr' ? 'Bifacial' : 'TopCon'}
                                      </button>
                                    );
                                  })}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => removeSku(catIndex, skuIndex)}
                                title="Remove from kit"
                                style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', lineHeight: 1, flex: '0 0 auto' }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
          {isMobile && (
            <div className="m-sticky-actions">
              <Link to="/kits" className="btn btn-secondary" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}>Cancel</Link>
              <button type="submit" disabled={loading} className="btn btn-success">
                {loading ? (isEdit ? 'Updating...' : 'Creating...') : saveLabel}
              </button>
            </div>
          )}
          {!isMobile && (
            <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900, background: 'white', borderTop: '1px solid #dee2e6', boxShadow: '0 -4px 12px rgba(0,0,0,0.06)', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: '#495057', marginRight: 'auto' }}>
                {checks.length ? `${checks.length} check${checks.length === 1 ? '' : 's'} to review (won't block saving)` : 'No problems found'}
                {stockHubName && !stockLoading ? ` · stock shown for ${stockHubName}` : ''}
              </span>
              <Link to="/kits" className="btn btn-secondary" style={{ pointerEvents: loading ? 'none' : 'auto', opacity: loading ? 0.6 : 1 }}>Cancel</Link>
              <button type="submit" disabled={loading} className="btn btn-success">
                {loading ? (isEdit ? 'Updating...' : 'Creating...') : saveLabel}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Warehouse Selection Modal for Bulk Update */}
      {showWarehouseModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              maxWidth: '780px',
              width: '92%',
              maxHeight: '85vh',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Modal Header */}
            <div style={{ padding: '20px 28px', borderBottom: '1px solid #e9ecef', background: '#f8f9fa' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                  Select Target Warehouses
                </h3>
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#0d6efd', background: '#e7f1ff', padding: '4px 12px', borderRadius: '12px' }}>
                  {selectedWarehouseIds.length} of {selectableWarehouses.length} Selected
                </span>
              </div>
              <p style={{ margin: '6px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                Choose the warehouses where you want to apply these custom kit configuration updates.
              </p>
              {(() => {
                const changed = getChangedFields(pendingPayload);
                if (!changed) return null;
                const changedLabels = changed.filter((k) => k !== 'variants' || !changed.includes('categories')).map((k) => KIT_FIELD_LABELS[k]);
                if (imageFile) changedLabels.push('Image');
                const keptLabels = Object.keys(KIT_FIELD_LABELS).filter((k) => !changed.includes(k)).map((k) => KIT_FIELD_LABELS[k]);
                return (
                  <div style={{ marginTop: '12px', fontSize: '13px', lineHeight: 1.5, background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px' }}>
                    <div style={{ color: '#0f172a' }}>
                      <strong>Will change:</strong> {changedLabels.length ? changedLabels.join(', ') : 'Nothing (only adds the kit to newly ticked hubs)'}
                    </div>
                    <div style={{ color: '#64748b' }}>
                      <strong>Not touched:</strong> {keptLabels.join(', ')}. Each hub keeps its own values for these.
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Modal Body */}
            <div
              style={{
                padding: '24px 28px',
                maxHeight: '480px',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }}
            >
              {warehousesLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#0d6efd', fontWeight: 500, fontSize: '15px' }}>
                  <div style={{ display: 'inline-block', width: '28px', height: '28px', border: '3px solid #b6d4fe', borderTop: '3px solid #0d6efd', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: '10px' }} />
                  <div>Loading warehouses list...</div>
                </div>
              ) : (
                <>
                  {/* Select All Row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      background: '#f1f5f9',
                      borderRadius: '8px',
                      marginBottom: '16px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 600, fontSize: '14px', margin: 0, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={selectableWarehouses.length > 0 && selectedWarehouseIds.length === selectableWarehouses.length}
                        onChange={handleToggleSelectAllWarehouses}
                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#0d6efd' }}
                      />
                      Select All Batch Warehouses
                    </label>
                    <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                      Only hubs in this batch are editable
                    </span>
                  </div>

                  {/* Warehouses Checkbox Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '10px' }}>
                    {allWarehousesList.map((wh) => {
                      const isBatchHub = batchWarehouseIds.length === 0 || batchWarehouseIds.includes(wh.id);
                      const isChecked = selectedWarehouseIds.includes(wh.id);
                      return (
                        <label
                          key={wh.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            border: isChecked ? '1px solid #0d6efd' : '1px solid #e2e8f0',
                            background: isChecked ? '#eff6ff' : '#ffffff',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            opacity: 1,
                            fontSize: '14px',
                            userSelect: 'none',
                          }}
                          title={!isBatchHub ? 'Click to add this newly created hub to this kit batch' : ''}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleSingleWarehouse(wh.id)}
                            style={{ width: '17px', height: '17px', cursor: 'pointer', accentColor: '#0d6efd' }}
                          />
                          <span style={{ fontWeight: isChecked ? 600 : 500, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', width: '100%', justifyContent: 'space-between' }}>
                            <span>
                              {wh.name} <span style={{ color: '#64748b', fontSize: '12px' }}>({wh.city})</span>
                              {(wh.hub_type === 'VIRTUAL' || wh.hubType === 'VIRTUAL') && (
                                <span style={{ display: 'block', color: '#64748b', fontSize: '11px', fontWeight: 400 }}>
                                  (Virtual Hub)
                                </span>
                              )}
                            </span>
                            {isBatchHub && (
                              <span style={{ fontSize: '10px', background: '#d1e7dd', color: '#0f5132', padding: '2px 8px', borderRadius: '10px', fontWeight: 600 }}>
                                Batched Hub
                              </span>
                            )}
                            {!isBatchHub && (
                              <span style={{ fontSize: '10px', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '10px', fontWeight: 500 }}>
                                + Add to Batch
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    })}
                  </div>

                  {selectedWarehouseIds.length === 0 && (
                    <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '16px', fontWeight: 600, textAlign: 'center', background: '#fff5f5', padding: '10px', borderRadius: '6px' }}>
                      ⚠️ Please select at least one warehouse to update.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: '16px 28px',
                borderTop: '1px solid #e9ecef',
                background: '#f8f9fa',
                display: 'flex',
                justify: 'flex-end',
                gap: '12px',
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowWarehouseModal(false)}
                style={{ fontSize: '14px', padding: '8px 20px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                disabled={selectedWarehouseIds.length === 0 || loading}
                onClick={handleBulkUpdateSubmit}
                style={{ fontSize: '14px', padding: '8px 24px', fontWeight: 600 }}
              >
                Confirm & Update Kit ({selectedWarehouseIds.length} Hubs)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateKit;
